<template>
  <div class="sub-category-waperCorners-page container">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderCommunity :page="currentPageInfo" />
    <SearchResults
      :is-loading="isLoading"
      placeholder-text="Rechercher un contenu"
      @search-update="searchUpdateHandler"
    >
      <template #results>
        <CardContainer
          :items="communityWaperCornersSubcategories"
          :no-result-text="noContent"
        />
      </template>
    </SearchResults>
    <ThePagination
      :total-pages="communityWaperCornersPages"
      :current-page="communityWaperCornersCurrentPage"
      :go-to="changePageTo"
    />
    <PegiWarning v-if="community?.pegi === '18'" />
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  middleware: async (to) => {
    const loadWapersCornerCommunitySubCategory =
      useLoadWapersCornerCommunitySubCategory()
    return loadWapersCornerCommunitySubCategory(to)
  },
})

const waperCornersStore = useWaperCornersStore()
const {
  communityWaperCornersPages,
  communityWaperCornersCurrentPage,
  communityWaperCornersSubcategories,
} = storeToRefs(waperCornersStore)

const link = useLinkPrevNext({
  currentPage: communityWaperCornersCurrentPage,
  pagesCount: communityWaperCornersPages,
})

const communitiesStore = useCommunitiesStore()
const { community } = storeToRefs(communitiesStore)
const pageTitle = computed(() => {
  return subcategorySlug.value ?? 'Le feed des players'
})
const arianeRoute = computed(() => {
  return [
    {
      title: 'Toutes les communautés',
      route: '/communautes',
    },
    {
      title: community.value?.title,
      route: `/communautes/${community.value?.slug}`,
    },
    {
      title: 'Le feed des players',
      route: `/communautes/${community.value?.slug}/le-feed-des-players`,
    },
    {
      title: pageTitle.value,
      route: '',
    },
  ]
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

const route = useRoute()
const slug = computed(() => {
  return route.params.subCategoryName as string
})

const subCategory = computed(
  () => waperCornersStore.getSubCategoryBySlug(slug).value,
)
const subcategorySlug = computed(() => subCategory.value?.slug)

useHead({
  title: `Discussion et décryptages « ${subcategorySlug.value} » des Wapers sur ${community.value?.title}`,
  link,
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: `Discussion et décryptages « ${subcategorySlug.value} » des Wapers sur ${community.value?.title}`,
  description: `Découvrez toutes les discussions, avis et décryptages « ${subcategorySlug.value} » sur ${community.value?.title} : infos, avis, forum, challenges et vidéos.`,
  ogTitle: `Tout le feed des players « ${subcategorySlug.value} » sur {nom_communaute}.`,
  ogDescription: `Découvrez toutes les discussions, avis et décryptages « ${subcategorySlug.value} » sur ${community.value?.title}.`,
  ogImage:
    community.value?.image_desktop?.url ??
    'https://www.weareplaystation.fr/img/og-img.jpg',
})

const referentialStore = useReferentialStore()
const { referential } = storeToRefs(referentialStore)

const currentPageInfo = computed(() => {
  const { image, imageMobile } = getImages(community)

  return {
    subCategory: pageTitle.value,
    title: 'Le feed des players',
    game: community.value?.title,
    pegi: community.value?.pegi,
    members: community.value?.users_count,
    image,
    imageMobile,
    contribPoints: referential.value?.points.post_source_create,
    description: `Découvrez toutes les discussions, avis et décryptages « ${subcategorySlug.value} » sur ${community.value?.title}.`,
  }
})

const searchData = async () => {
  isLoading.value = true

  await waperCornersStore.fetchCommunitySubcategoriesWaperCorners({
    page: 1,
    search: search.value,
    community: route.params.communityName as string,
    subCategory: slug.value,
  })

  if (!communityWaperCornersSubcategories.value.length) {
    noContent.value = !search.value
      ? 'Aucun contenu'
      : 'Aucun résultat pour votre recherche'
  }

  isLoading.value = false
}

const { search, isLoading, noContent, searchUpdateHandler } = useSearching({
  searchData,
})

const changePageTo = async (index = 1) => {
  if (index > 0) {
    await waperCornersStore.fetchCommunitySubcategoriesWaperCorners({
      page: index,
      search: search.value,
      community: route.params.communityName as string,
      subCategory: slug.value,
    })
  }
}
</script>

<style lang="scss" scoped>
.sub-category-waperCorners-page {
  min-height: 100vh;
  padding-bottom: 10rem;
}
</style>
